import { ArrowBack } from "@material-ui/icons";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  Divider,
  IconButton,
} from "@mui/material";
import axios from "axios";
import {
  CustomTable,
  ModalError,
  SearchTextInput,
  SecondaryButton,
  SkeletonComponent,
} from "components";
import { InnoTableV2 } from "inno-ui";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { getErrors } from "utils";
import {
  columnTableComposerInvoice,
  columnTableSongResults,
  columnTableInvoice,
} from "./Constant";

export const ModalDetailRoyaltyTracking = ({
  open,
  onClose,
  preload,
  loadingButton,
}) => {
  const classes = globalStyles();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
    search: "",
    sort: "",
  });
  const [loadingPage, setLoadingPage] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };

  const getDataTable = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(
        `${hardBaseUrl}/publisher/royalty-tracking/v1/sales-invoice/${preload?.sales_invoice_id}/composer/${preload?.composer_id}`,
        {
          headers,
          params: queryParams,
        }
      );
      const { data, meta } = res?.data;

      setDataTable(data || []);
      const pageCount = getTotalPage(meta?.total, queryParams?.size);
      setTableTotalPage(pageCount || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const debounceDataTable = useCallback(
    debounce(() => {
      if (preload.composer_id) {
        getDataTable();
      }
    }, 500),
    [queryParams, preload.composer_id]
  );

  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      getDataTable();
    }
  }, [queryParams, debounceDataTable]);

  return (
    <>
      {loadingPage ? (
        <SkeletonComponent variant="wave" />
      ) : (
        <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose}>
          <DialogTitle>
            <Typography fontSize={16} fontWeight={700}>
              {preload.composer_name}
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item>
                <Grid container columnSpacing={1} alignItems="center">
                  <Grid item>
                    <SearchTextInput
                      placeholder="Search"
                      value={queryParams?.search}
                      onChange={e =>
                        handleChangeQueryParams(e?.target?.value, "search")
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Divider className={classes?.divider} />

            <InnoTableV2
              isLoading={false}
              columns={columnTableSongResults}
              items={dataTable || []}
              page={queryParams?.page}
              rowsPerPage={queryParams?.size}
              totalPage={tableTotalPage}
              handleChangePage={(_, page) =>
                handleChangeQueryParams(page, "page")
              }
              handleChangeRowsPerPage={e =>
                handleChangeQueryParams(e?.target?.value, "size")
              }
            />
          </DialogContent>
          <DialogActions>
            <SecondaryButton
              onClick={onClose}
              label="Cancel"
              disabled={loadingButton}
            />
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export const ModalTotalRoyaltyTracking = ({
  open,
  onClose,
  title,
  params,
  filter,
}) => {
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const apiURL = `publisher/royalty-tracking/v1/sales-invoice/list`;
  const apiURLComposerInvoice = `publisher/transaction/v2/composer-invoice`;

  const [loadingPage, setLoadingPage] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
    search: "",
    year: params?.year,
    status: filter === "paid" ? "paid" : "",
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const getDataTable = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(
        `${hardBaseUrl}/${filter === "paid" ? apiURLComposerInvoice : apiURL}`,
        {
          headers,
          params: queryParams,
        }
      );
      const { data, meta } = res?.data;
      setDataTable(filter === "paid" ? data : data?.list);
      const pageCount = getTotalPage(meta?.total, queryParams?.size);
      setTableTotalPage(pageCount || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const debounceDataTable = useCallback(
    debounce(() => {
      getDataTable();
    }, 500),
    [queryParams]
  );

  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      getDataTable();
    }
  }, [queryParams, debounceDataTable]);

  return (
    <Dialog
      fullWidth={true}
      maxWidth="lg"
      open={open}
      onClose={onClose}
      disableEscapeKeyDown={true}
    >
      <DialogTitle>
        <Grid container columnGap={1}>
          <Grid item>
            <IconButton onClick={onClose}>
              <ArrowBack />
            </IconButton>
          </Grid>
          <Grid item>
            <Typography fontSize={24} fontWeight={700}>
              {title}
            </Typography>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <SearchTextInput
          fullWidth
          placeholder="Search Invoice Number"
          value={queryParams?.search}
          onChange={e => {
            handleChangeQueryParams(e?.target?.value, "search");
          }}
        />
        <Box mt="24px">
          <CustomTable
            columnTable={
              filter === "paid"
                ? columnTableComposerInvoice()
                : columnTableInvoice({
                    isModalTable: true,
                    isStatus: filter,
                  })
            }
            data={dataTable}
            items={dataTable}
            page={queryParams?.page}
            pageSize={queryParams?.size}
            pageCount={tableTotalPage}
            handleChangePage={(_, page) =>
              handleChangeQueryParams(page, "page")
            }
            handleChangePageSize={e =>
              handleChangeQueryParams(e?.target?.value, "size")
            }
            isLoading={loadingPage}
            maxHeight={400}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};
